/**
 * ExpiredGuard — wraps protected routes so a trial org whose LIVE
 * subscription_state is "trial_expired" lands on a "Trial expired —
 * pick a plan" interstitial instead of seeing the dashboard / clients /
 * notices / any other product surface.
 *
 * Placement: nested inside <OnboardingGuard> so the onboarding
 * redirect (choose-path) runs first. Order is:
 *   ProtectedRoute  →  OnboardingGuard  →  ExpiredGuard  →  MainLayout
 *
 * Exemptions: the same routes that stay reachable during onboarding,
 * because a trial-expired user must still be able to buy a plan,
 * view their profile, and log out. /embed bypasses entirely because
 * partner-SSO sessions are paid-by-partner and never on a trial.
 *
 * Partner / admin roles bypass — the BE dependency self-bypasses for
 * them too, so the UX matches the API contract.
 */
import { Navigate, useLocation } from "react-router-dom";
import { Button, Result, Spin } from "antd";
import { ShoppingCartOutlined } from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import { useSubscriptionStatus } from "../../hooks/useSubscriptionStatus";
import { isPartnerSsoSession } from "../../utils/embedFlow";

// Routes that stay reachable when a trial is expired. Mirrors the
// OnboardingGuard allow-list intentionally — the two guards permit
// the same escape hatches.
const EXPIRED_EXEMPT_PREFIXES = [
  "/onboarding",
  "/shop",
  "/usage",
  "/my-account-profile-mv",
  "/logout",
  "/embed",
];

// Roles that should never be locked out — they're either viewing the
// expired tenant in an admin context (super_admin) or operate outside
// the customer subscription model (partner staff).
const BYPASS_ROLES = new Set([
  "super_admin",
  "partner_admin",
  "partner_admin_user",
]);

const ExpiredGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { status, isLoading, error } = useSubscriptionStatus();

  if (!user) return children;
  if (isPartnerSsoSession()) return children;
  if (user.role && BYPASS_ROLES.has(user.role)) return children;

  if (
    EXPIRED_EXEMPT_PREFIXES.some((prefix) =>
      location.pathname.startsWith(prefix),
    )
  ) {
    return children;
  }

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Fail open on a BE error reaching /subscriptions/status — better
  // to briefly serve a paid user the dashboard than lock everyone out
  // on a transient API blip. The BE dependency is the real gate.
  if (error) return children;

  // Decide from the LIVE subscription_state — the same state machine the
  // backend enforces mutating requests with — never from the nightly
  // trial_expired_at stamp. The stamp survives trial restarts and admin
  // extensions (it lags by design), and keying the guard off it locked
  // out orgs whose trial was genuinely active (stale stamp from an
  // earlier trial round) while the navbar showed "11 days left".
  const isExpired = !!status && status.subscription_state === "trial_expired";

  if (!isExpired) return children;

  // Render an inline interstitial instead of <Navigate> so the user
  // doesn't get stuck in a redirect loop if something else tries to
  // route them away. Single CTA: go buy a plan.
  return (
    <div
      style={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Result
        status="warning"
        title="Your free trial has expired"
        subTitle="Pick a plan from the Shop to restore access to your dashboard, clients, and notices."
        extra={
          <Button
            type="primary"
            size="large"
            icon={<ShoppingCartOutlined />}
            onClick={() => {
              window.location.href = "/shop";
            }}
          >
            Go to Shop
          </Button>
        }
      />
    </div>
  );
};

export default ExpiredGuard;
